import { IntlLink } from "@/i18n/navigation";


export type PageHref = React.ComponentProps<typeof IntlLink>["href"];

export const Nbsp = '\u00A0';

export type Milliseconds = number & {};


export async function drawingComplete(extraDelay: Milliseconds = 0): Promise<void> {
  await delay(extraDelay);
  await nextFrame();
}


export function nextFrame() {
  return new Promise<void>(resolve => {
    requestAnimationFrame(() => resolve());
  });
}


export function delay(delay: Milliseconds) {
  return new Promise<void>(resolve => setTimeout(resolve, delay));
}


type CssDisplayPrimitive = "block" | "flex" | "grid";
type CssDisplay = CssDisplayPrimitive | `inline-${CssDisplayPrimitive}`;

const XlDisplayMap = {
  "block": "xl:block",
  "flex": "xl:flex",
  "grid": "xl:grid",
  "inline-block": "xl:inline-block",
  "inline-flex": "xl:inline-flex",
  "inline-grid": "xl:inline-grid",
} as const satisfies {
  [k in CssDisplay]: string;
};

export function desktopOnly(display: CssDisplay) {
  return `hidden ${XlDisplayMap[display]}` as const;
}

export function mobileOnly() {
  return "xl:hidden" as const;
}


export function groupBy<T>(size = 2, items: T[]): T[][] {
  const result: T[][] = [];

  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }

  return result;
}


export function getNumberFormat() {
  const formatNumber = new Intl.NumberFormat().format;
  const sampleNumber = formatNumber(1000.10);

  const thousandSeparator = sampleNumber.replace(/\d/g, "").charAt(0) ?? ",";
  const decimalSeparator = sampleNumber.replace(/\d/g, "").charAt(1) ?? ".";

  const createFormatter = (options: Parameters<typeof Intl["NumberFormat"]>[1]) => (
    new Intl.NumberFormat(undefined, options).format
  );

  return {
    formatNumber,
    decimalSeparator,
    thousandSeparator,
    createFormatter,
  };
}


export function when<T, U>(option: T | undefined | null, then: (value: T) => U): U | undefined {
  if (option === undefined || option === null) {
    return;
  }

  return then(option);
}


export type Result<T, E> = (
  | { ok: true; value: T }
  | { ok: false; error: E }
);

export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
};

export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
};

export function safeTry<T>(runnable: () => T): Result<T, unknown> {
  try {
    return ok(runnable());

  } catch (error) {
    return err(error);
  }
}

export async function safeTryAsync<T>(runnable: () => Promise<T>): Promise<Result<T, unknown>> {
  try {
    return ok(await runnable());

  } catch (error) {
    return err(error);
  }
}


export function transform<T, U>(value: T, transform: (value: T) => U): U {
  return transform(value);
}


export function call<T>(runnable: () => T): T {
  return runnable();
}
